import { Component, OnDestroy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { combineLatest, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { HamptonComparable } from "src/app/core/valuation-process/_models/hampton-comparable.model";
import { selectHamptonAnalysis, selectHamptonAnalysisValid } from "src/app/core/valuation-process/_selectors/hampton-valuation.selectors";
import { selectValuationProcessSelectedComparablesInHampton, selectValuationProcessTargetPropertyUnitMAcronym } from "src/app/core/valuation-process/_selectors/valuation-process.selectors";
import * as _ from 'lodash'
import { HamptonValuationUpdateAnalysis } from "src/app/core/valuation-process/_actions/hampton-valuation.actions";
import { selectValuationProcessValuationSummary } from "src/app/core/valuation-process/_selectors/valuation.selectors";
import { selectValuationProcessConsiderationCriterion } from "src/app/core/valuation-process/_selectors/consideration-criterion.selector";

@Component({
  selector: 'app-hampton-analysis',
  templateUrl: './hampton-analysis.component.html',
  styleUrls: ['./hampton-analysis.component.scss']
})
export class HamptonAnalysisComponent implements OnDestroy {
  form: UntypedFormGroup

  unitMeasurement$ = this.store$.select(selectValuationProcessTargetPropertyUnitMAcronym)

  viewModel$ = combineLatest([
    this.unitMeasurement$
  ]).pipe(
    map(([unit]) => {
      return {
        unit
      }
    })
  )

  private _comparables: any[] = []
  private state$ = combineLatest([
    this.store$.select(
      selectHamptonAnalysis
    ),
    this.store$.select(
      selectValuationProcessSelectedComparablesInHampton
    )
  ]).pipe(
    map(([state, comparables]) => {
      const isChanged = !checkDiff(this._comparables, comparables)
      this._comparables = comparables
      return {
        state, 
        comparables,
        changed: isChanged
      }
    })
  )

  totalValue$ = this.store$.select(selectValuationProcessValuationSummary).pipe(
    map(summary => {
      return summary.hamptonTotalValue
    })
  )
  currency$ = this.store$.select(selectValuationProcessConsiderationCriterion).pipe(
    map(crit => {
      return ` ${crit.countryCurrency}`
    })
  )

  private _onDestroy$ = new Subject<void>()

  constructor(
    private store$: Store,
    private formBuilder: UntypedFormBuilder
  ) {
    this.form = this.formBuilder.group({
      comparable_lower_end: this.formBuilder.control(null, [Validators.required]),
      comparable_higher_end: this.formBuilder.control(null, [Validators.required]),
      consultant_lower_end: this.formBuilder.control(null),
      consultant_higher_end: this.formBuilder.control(null),
      valuer_lower_end: this.formBuilder.control(null),
      valuer_higher_end: this.formBuilder.control(null),
      reasoning: this.formBuilder.control(null, [Validators.required]),
      value_range_id: this.formBuilder.control(null, [Validators.required]),
      value_estimated: this.formBuilder.control(null, [Validators.required]),
      rental_value_lower_end: this.formBuilder.control(null, [Validators.required]),
      rental_value_higher_end: this.formBuilder.control(null, [Validators.required]),
      land_value: this.formBuilder.control(null)
    })

    this.effect()

    this.state$.pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(s => {
      console.log(s)
      const [low, high] = this.findLowerAndHigher(s.comparables)
      const comparableLowerEnd = s.changed
        ? low 
        : s.state.comparablesPriceLowerEnd !== null 
          ? s.state.comparablesPriceLowerEnd
          : low
      const comparableHigherEnd = s.changed 
        ? high
        : s.state.comparablesPriceHigherEnd !== null
          ? s.state.comparablesPriceHigherEnd
          : high
      
      this.form.patchValue({
        comparable_lower_end: low,
        comparable_higher_end: high,
        consultant_lower_end: s.state.consultantLower,
        consultant_higher_end: s.state.consultantHigher,
        valuer_lower_end: s.state.valuerLower,
        valuer_higher_end: s.state.valuerHigher,
        reasoning: s.state.reasoning,
        value_range_id: s.state.valueRangeId,
        value_estimated: s.state.valueEstimated,
        rental_value_lower_end: s.state.rentalValueLower,
        rental_value_higher_end: s.state.rentalValueHigher,
        land_value: s.state.landValue
      }, {emitEvent: false})
    })
  }

  ngOnDestroy(): void {
    this._onDestroy$.next()
    this._onDestroy$.complete()
  }

  private effect() {
    this.form.valueChanges.pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(value => {
      this.store$.dispatch(new HamptonValuationUpdateAnalysis({
        comparablesPriceLowerEnd: value.comparable_lower_end,
        comparablesPriceHigherEnd: value.comparable_higher_end,
        consultantLower: value.consultant_lower_end,
        consultantHigher: value.consultant_higher_end,
        valuerLower: value.valuer_lower_end,
        valuerHigher: value.valuer_higher_end,
        reasoning: value.reasoning,
        valueRangeId: value.value_range_id,
        valueEstimated: value.value_estimated,
        rentalValueLower: value.rental_value_lower_end,
        rentalValueHigher: value.rental_value_higher_end,
        landValue: value.land_value
      }))
    })
  }

  private findLowerAndHigher(comparables: HamptonComparable[]) {
    const values = comparables.map(com => {
      const bua = com.size === 0 ? 0 : com.price / com.size
      return bua
    })

    return [_.min(values), _.max(values)]
  }
}

function checkDiff(prev: HamptonComparable[], curr: HamptonComparable[]) {
  const prevIds = prev.map(item => item.id)
  const currentIds = curr.map(item => item.id)

  if (prevIds.length !== currentIds.length) return false;
  return prevIds.every((id, i) => id === currentIds[i]);
}